import { useState } from "react";
import { srcSetFor } from "@/lib/responsiveImage";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";

import PageHero from "@/components/PageHero";
import { FadeUp } from "@/components/Motion";
import SeoHead from "@/components/SeoHead";
import { SectionLoading, SectionError, SectionEmpty } from "@/components/RichContent";
import { useCaseStudies } from "@/hooks/usePublicData";

const ALL = "All";

export default function CaseStudies() {
  const [category, setCategory] = useState(ALL);

  const { data, isLoading, isError, refetch, isFetching } = useCaseStudies({
    pageSize: 24,
    category: category === ALL ? undefined : category,
  });

  const items = (data && data.items) || [];
  const categories = [ALL, ...((data && data.categories) || [])];

  return (
    <>
      <SeoHead pageKey="case_studies" />

      <PageHero
        eyebrow="Case Studies"
        chapter="Chapter — Selected Engagements"
        lines={["Every Property Is Unique.", "So Is Every Client."]}
        blurb="No two properties are exactly alike — and no two clients have the same circumstances, objectives or priorities."
        image="/assets/hero-case-studies"
        imageAlt="An open property research report showing project photographs and transaction charts, on a desk overlooking the Singapore skyline"
      />

      <section className="bg-[#060f1e] pt-20 lg:pt-28">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <FadeUp>
            <div className="max-w-3xl space-y-6 text-[#d8d8d8] leading-relaxed">
              <p>
                A property decision is influenced by far more than location and price. The right outcome
                depends on the client's financial position, financing structure, CPF considerations,
                investment horizon, risk appetite, existing portfolio, future plans and personal
                objectives. The same property may therefore represent a compelling opportunity for one
                client, but an entirely different proposition for another.
              </p>
              <p className="text-[#f7f5f0]">
                That is why PIRAC does not believe in standardised recommendations or one-size-fits-all
                solutions.
              </p>
              <p>
                Every engagement begins with understanding the individual circumstances and objectives of
                the client, followed by research specifically tailored to the property, market and
                decision at hand. We examine the relevant data, comparable transactions, market
                conditions, financial implications, risks and potential scenarios before arriving at our
                assessment.
              </p>
              <p>
                Our research is designed to answer the questions that matter most to the individual
                client — not simply what a property is worth today, but whether it is suitable for them,
                what risks they may be taking, what alternatives may exist and how the decision could
                affect their broader financial position over time.
              </p>
              <p>
                This customised approach enables us to identify appropriate options rather than simply
                available options.
              </p>
              <p>
                We believe good property advice is not about finding the most popular property or the
                highest potential return. It is about finding the option that best fits the client's
                circumstances, objectives and long-term strategy.
              </p>
            </div>
          </FadeUp>
          <FadeUp delay={0.1}>
            <div className="mt-10 pt-8 border-t border-[#1a2940] max-w-3xl">
              <p className="font-serif text-2xl text-[#f7f5f0] leading-snug">
                Every property is unique. Every client is different.
                <br />
                Our research is customised accordingly.
              </p>
              <div className="mt-6 overline text-[#cca24d]/80">
                Research with care. Analyse with perspective. Recommend with purpose.
              </div>
            </div>
          </FadeUp>
        </div>
      </section>

      <section className="bg-[#060f1e] py-16 border-b border-[#1a2940]">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 flex flex-wrap gap-3" data-testid="case-filters">
          {categories.map((c) => (
            <button
              key={c}
              onClick={() => setCategory(c)}
              data-testid={`filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
              className={`px-5 h-11 text-sm tracking-wide border transition-all duration-300 ${
                category === c
                  ? "border-[#cca24d] bg-[#cca24d]/10 text-[#cca24d]"
                  : "border-[#1a2940] text-[#d8d8d8] hover:border-[#cca24d]/40 hover:text-[#cca24d]"
              }`}
            >
              {c}
            </button>
          ))}
        </div>
      </section>

      <section className="bg-[#060f1e] py-16 lg:py-24">
        {isLoading ? (
          <SectionLoading label="Loading engagements" />
        ) : isError ? (
          <SectionError onRetry={refetch} />
        ) : (
          <div
            className={`mx-auto max-w-[1400px] px-6 lg:px-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6
              transition-opacity duration-200 ${isFetching ? "opacity-60" : "opacity-100"}`}
          >
            {items.length === 0 ? (
              <SectionEmpty
                title="No engagements in this category yet"
                description="Case studies are published once the client has approved the anonymised write-up."
              />
            ) : (
              items.map((cs, i) => (
                <FadeUp key={cs.id} delay={Math.min(i, 6) * 0.08}>
                  <Link
                    to={cs.path}
                    className="group block h-full"
                    data-testid={`case-${cs.slug}`}
                  >
                    <div className="relative overflow-hidden aspect-[4/5] bg-[#0c1728]">
                      {cs.featuredImage && (
                        <img
                          src={cs.featuredImage}
                          srcSet={srcSetFor(cs.featuredImage)}
                          sizes="(min-width: 1024px) 45vw, 90vw"
                          alt={cs.featuredImageAlt || cs.title}
                          className="absolute inset-0 w-full h-full object-cover opacity-70 group-hover:opacity-100 group-hover:scale-105 transition-all duration-1000"
                          loading="lazy"
                        />
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-[#060f1e] via-[#060f1e]/30 to-transparent" />
                      <div className="absolute inset-x-0 bottom-0 p-8">
                        {cs.category && (
                          <div className="text-xs text-[#cca24d] tracking-widest uppercase mb-3">
                            {cs.category}
                          </div>
                        )}
                        <h3 className="font-serif text-2xl lg:text-3xl text-[#f7f5f0] leading-tight">
                          {cs.title}
                        </h3>
                        {cs.summary && (
                          <p className="mt-3 text-sm text-[#d8d8d8] line-clamp-2">{cs.summary}</p>
                        )}
                        <div className="mt-6 flex items-center gap-2 text-sm text-[#f7f5f0] group-hover:text-[#cca24d] transition-colors">
                          Read <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={1.5} />
                        </div>
                      </div>
                    </div>
                  </Link>
                </FadeUp>
              ))
            )}
          </div>
        )}
      </section>
    </>
  );
}
